import React from 'react';
import './ConnectButton.css';
import { setIntegrationSource } from '../../utils/integrationTracking';

export interface ConnectButtonProps {
  /**
   * Texto do botão
   */
  children: React.ReactNode;

  /**
   * Se o botão está desabilitado
   */
  disabled?: boolean;

  /**
   * Variante do botão
   */
  variant?: 'primary' | 'secondary' | 'coming-soon';

  /**
   * Tamanho do botão
   */
  size?: 'small' | 'medium' | 'large';

  /**
   * Função chamada quando o botão é clicado
   */
  onClick?: () => void;

  /**
   * ID da integração (para futuras funcionalidades UTM)
   */
  integrationId?: string;

  /**
   * Classes CSS adicionais
   */
  className?: string;

  /**
   * Atributos HTML adicionais
   */
  [key: string]: any;
}

/**
 * Componente reutilizável para botões de conexão
 *
 * @example
 * ```tsx
 * <ConnectButton
 *   variant="primary"
 *   onClick={() => handleConnect()}
 *   integrationId="salesforce"
 * >
 *   Conectar
 * </ConnectButton>
 * ```
 */
const ConnectButton: React.FC<ConnectButtonProps> = ({
  children,
  disabled = false,
  variant = 'primary',
  size = 'medium',
  onClick,
  integrationId,
  className = '',
  ...props
}) => {
  // Determinar se está em modo "coming soon" baseado na variante
  const isComingSoon = variant === 'coming-soon';
  const isDisabled = disabled || isComingSoon;

  // Classes CSS baseadas nas props
  const buttonClasses = [
    'connect-button',
    `connect-button--${variant}`,
    `connect-button--${size}`,
    isDisabled ? 'connect-button--disabled' : '',
    className
  ].filter(Boolean).join(' ');

  // Texto baseado na variante
  const buttonText = isComingSoon ? 'Notificar-me' : children;

  // Handler do clique
  const handleClick = () => {
    if (isDisabled) return;
    // NOVO: guardar a integração assim que clicam
    if (integrationId) setIntegrationSource(integrationId);
    onClick?.();
  };

  return (
    <button
      className={buttonClasses}
      disabled={isDisabled}
      onClick={handleClick}
      data-integration-id={integrationId}
      aria-label={isComingSoon ? 'Notificar quando disponível' : 'Conectar integração'}
      {...props}
    >
      {buttonText}
    </button>
  );
};

export default ConnectButton;
